import L from 'leaflet';
import { tenantApi } from '@shared/api/tenantApi';
import { unwrap } from '@shared/api/unwrap';

/** What `GET /geo/basemap` answers: the background every map draws. */
export interface Basemap {
  kind: 'vector' | 'raster';
  url: string;
  attribution: string;
  max_zoom: number;
  flavor?: string;
  /** A logo the provider's terms require on the map (Google). */
  logo_url?: string;
  /** Where the credit for the area in view comes from, for a provider whose credit changes as the map moves. */
  attribution_url?: string;
}

/** What maps drew before the Mapping module, and what they fall back to on any error. */
const PUBLIC_OSM_BASEMAP: Basemap = {
  kind: 'raster',
  url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
  attribution: '&copy; OpenStreetMap contributors',
  max_zoom: 19,
};

/** The tile URL's token lasts a day; an hour keeps every map well inside it. */
const REFRESH_MS = 60 * 60 * 1000;

let cached: { at: number; basemap: Promise<Basemap> } | undefined;

/** One lookup serves every map for an hour; a failed one is asked again next time. */
function current(): Promise<Basemap> {
  if (!cached || Date.now() - cached.at > REFRESH_MS) {
    const basemap = tenantApi.get('/geo/basemap').then(({ data }) => unwrap<Basemap>(data));
    basemap.catch(() => (cached = undefined));
    cached = { at: Date.now(), basemap };
  }
  return cached.basemap;
}

/** Vector tiles are drawn by protomaps-leaflet, loaded only when a map needs it; raster tiles are images. */
async function toLayer(basemap: Basemap): Promise<L.Layer> {
  if (basemap.kind === 'vector') {
    const { leafletLayer } = await import('protomaps-leaflet');
    return leafletLayer({
      url: basemap.url,
      flavor: basemap.flavor ?? 'light',
      attribution: basemap.attribution,
      maxDataZoom: basemap.max_zoom,
      maxZoom: 19,
    }) as unknown as L.Layer;
  }
  return L.tileLayer(basemap.url, { attribution: basemap.attribution, maxZoom: basemap.max_zoom });
}

/**
 * What a provider's terms ask to be shown on its map: its logo, bottom left, and the credit for the
 * area in view, kept current as the map moves. Google asks for both.
 */
function addProviderCredits(map: L.Map, basemap: Basemap): void {
  if (basemap.logo_url) {
    const logo = new L.Control({ position: 'bottomleft' });
    logo.onAdd = () => {
      const img = L.DomUtil.create('img') as HTMLImageElement;
      img.src = basemap.logo_url!;
      img.alt = 'Google';
      img.style.height = '18px';
      return img;
    };
    logo.addTo(map);
  }

  if (basemap.attribution_url) {
    let shown = basemap.attribution;
    let asked = 0;
    const refresh = () => {
      // Only the answer for the latest view is shown; one for an earlier view arriving late is dropped.
      const ask = ++asked;
      const b = map.getBounds();
      const url = `${basemap.attribution_url}&zoom=${map.getZoom()}&north=${b.getNorth()}&south=${b.getSouth()}&east=${b.getEast()}&west=${b.getWest()}`;
      fetch(url)
        .then((res) => (res.ok ? res.json() : null))
        .then((body) => {
          const next: string | undefined = body?.data?.attribution;
          if (ask !== asked || !next || next === shown) return;
          map.attributionControl?.removeAttribution(shown).addAttribution(next);
          shown = next;
        })
        // The fixed credit already shows; a missed update leaves it standing rather than breaking the map.
        .catch(() => undefined);
    };
    map.whenReady(refresh);
    map.on('moveend', refresh);
  }
}

/**
 * Draws the organisation's basemap under a map (docs/mapping-channels-plan.md): the region's own
 * vector tiles, a tile provider it added, or OpenStreetMap's public tiles, as the API decides. It
 * arrives a moment after the map, and never fails to.
 */
export function addBasemap(map: L.Map): void {
  // A map removed before the basemap arrives must not be drawn on.
  let removed = false;
  map.once('unload', () => (removed = true));

  current()
    .then(async (basemap) => ({ basemap, layer: await toLayer(basemap) }))
    .catch(async () => ({ basemap: PUBLIC_OSM_BASEMAP, layer: await toLayer(PUBLIC_OSM_BASEMAP) }))
    .then(({ basemap, layer }) => {
      if (removed) return;
      layer.addTo(map);
      addProviderCredits(map, basemap);
    });
}
